import React from 'react';
import DatePicker from 'react-datepicker';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowDownIcon } from 'shared/ui/icons';
// PlusIcon import hidden with upload button — SA-1583 (restore both to re-enable)
import { Table } from 'shared/ui/table';
import useWindowDimensions from 'shared/hooks/useWindowDimensions';
import {
  getTariffBookColumns,
  mapTariffBookRows,
} from './tariff-book-table.columns';

const SearchIcon = (
  <svg
    className="w-5 h-5 text-gray-500"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1115 3.5a7.5 7.5 0 011.65 14.15z"
    />
  </svg>
);

const TariffBookTable = ({
  data,
  isFetching,
  search,
  pageSize,
  dateStart,
  dateEnd,
  handleSearchChange,
  handleSortOrderChange,
  handlePaginationChange,
  handlePageSizeChange,
  handleDateRangeChange,
  onUploadClick,
  canCreate,
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { isMobile } = useWindowDimensions();

  const columns = getTariffBookColumns(t);
  const rows = mapTariffBookRows(data, t, navigate);

  return (
    <div className="w-full bg-white border rounded-lg flex flex-wrap p-4">
      <div className="w-full flex flex-wrap justify-between flex-col-reverse md:flex-row gap-4">
        <DateRangeFilter
          dateStart={dateStart}
          dateEnd={dateEnd}
          isMobile={isMobile}
          onChange={handleDateRangeChange}
          t={t}
        />
        <SearchAndUpload
          search={search}
          isMobile={isMobile}
          onSearchChange={handleSearchChange}
          onUploadClick={onUploadClick}
          canCreate={canCreate}
          t={t}
        />
        <div className="w-full mt-6">
          <Table
            data={rows}
            columns={columns}
            pageSize={pageSize}
            loading={isFetching}
            page={data?.meta?.page}
            total={data?.meta?.total}
            limit={data?.meta?.limit}
            handlePageSizeChange={handlePageSizeChange}
            handlePaginationChange={handlePaginationChange}
            handleSortOrderChange={handleSortOrderChange}
          />
        </div>
      </div>
    </div>
  );
};

function DateRangeFilter({ dateStart, dateEnd, isMobile, onChange, t }) {
  return (
    <div
      className={`flex items-center ${isMobile ? 'flex-col-reverse flex-col w-full' : ''}`}
    >
      <div className="relative w-full md:w-auto">
        <DatePicker
          selectsRange={true}
          startDate={dateStart}
          endDate={dateEnd}
          onChange={(update) => onChange(update)}
          isClearable={true}
          className="w-full md:w-[250px] custom-datepicker rounded-md border px-4 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
          placeholderText={t('select_range_date')}
          dateFormat="dd/MM/yyyy"
        />
        <div
          className={`absolute inset-y-0 right-3 flex items-center pl-0 z-50 ${dateStart ? 'hidden' : ''}`}
        >
          <ArrowDownIcon width="24" height="24" color="#6b7280" />
        </div>
      </div>
    </div>
  );
}

function SearchAndUpload({
  search,
  isMobile,
  onSearchChange,
  // onUploadClick, canCreate — wiring for hidden upload button, uncomment with button (SA-1583)
  t,
}) {
  return (
    <div className="flex justify-end flex-wrap gap-2">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3">
          {SearchIcon}
        </div>
        <input
          type="text"
          className={`${isMobile ? 'w-full' : ''} pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
          placeholder={t('label_search')}
          defaultValue={search}
          onChange={onSearchChange}
          aria-label={t('label_search')}
        />
      </div>
      {/* SA-1583: upload button hidden per request — not removed, uncomment to restore.
      {canCreate ? (
        <button
          type="button"
          onClick={onUploadClick}
          className="min-w-max md:w-auto h-10 px-3 py-1 bg-blue-500 text-white flex justify-center items-center gap-2 rounded-md"
        >
          <PlusIcon color="white" /> {t('upload_tariff_book')}
        </button>
      ) : null} */}
    </div>
  );
}

export default TariffBookTable;
